import React, { useCallback, useState } from 'react';
import { CompositeScreenProps } from '@react-navigation/native';
import { Linking, Platform, Share, StyleSheet, View } from 'react-native';
import { Button, List, Snackbar, useTheme } from 'react-native-paper';
import Clipboard from '@react-native-clipboard/clipboard';
import { StackScreenProps } from '@react-navigation/stack';
import { Carousel } from 'react-responsive-carousel';
import { useI18n } from '../context/I18nContext';
import ScrollablePage from '../components/ui/ScrollablePage';
import SpaceSchedule from '../components/ui/SpaceSchedule';
import Spacer from '../components/Spacer';
import { useMixpanel } from '../mixpanel/MixpanelContext';
import { AmenityIcon } from './MapView/PodInformationCard';
import { MapStackRouteParams } from '../navigators/MapStackNavigator';
import { AppDrawerScreenProp } from '../components/DrawerMenuContent';
import { useGetSpaceInfo } from '../apis/mapApis';
import Images from '../../assets/images';
import { SpacePricing } from '../components/ui/SpacePricing';
import { BillingType, Currency } from '../types/appsync-types';
import Typography from '../components/Typography';
import { UiIcon, UiIconProps, uiIconSourceGenerator } from '../components/SimpleListIcon';
import GradientButton from '../components/GradientButton';
import { CardBadge } from '../components/CardBadge';
import { useParkingTermsUrlForCurrentLocale } from '../hooks/useParkingTermsUrlForCurrentLocale';
import { ImageWithFallback } from '../components/ImageWithFallback';
import { ImageCarousel } from '../components/ImageCarousel';
import { TextWrapper } from '../components/TextWrapper';

export type SpaceInfoRouteParams = { spaceId: string };

type Props = CompositeScreenProps<StackScreenProps<MapStackRouteParams, 'SpaceInfo'>, AppDrawerScreenProp>;

type Amenity = {
  icon: UiIconProps;
  title: string;
  tableTitle?: string;
  value?: string | number;
  skip?: boolean;
  bookAction?: () => void;
};

const AmenityItem: React.FC<{
  amenity: 'maxCapacity' | 'screen' | 'whiteboard' | 'usbPower' | 'powerOutlet' | 'bikeWash';
  title: string;
}> = ({ amenity, title }) => {
  const { colors } = useTheme();
  return (
    <List.Item
      style={{ paddingVertical: 0 }}
      titleStyle={{ fontSize: 14, color: colors.inverseOnSurface }}
      left={() => (
        <List.Icon icon={(props) => <AmenityIcon name={amenity} iconColor={colors.inverseOnSurface} {...props} />} />
      )}
      title={title}
      titleNumberOfLines={2}
    />
  );
};
export const SpaceInfo: React.FC<Props> = ({ navigation, route }) => {
  const { I18n } = useI18n();
  const { colors } = useTheme();
  const mp = useMixpanel();
  const [copiedSnackbarVisible, setCopiedSnackbarVisible] = useState<boolean>(false);
  const spaceInfoResult = useGetSpaceInfo(route.params?.spaceId);
  const spaceInfo = spaceInfoResult.data?.data?.getPoisByID;

  const parkingTermsUrl = useParkingTermsUrlForCurrentLocale(spaceInfo?.metadata?.parkingTerms);

  const handleSharePodDetails = useCallback(async () => {
    if (Platform.OS !== 'web') {
      const result = await Share.share(
        {
          title: `#${route.params?.spaceId}`,
          url: `https://app.biketti.tech/c/space/${route.params?.spaceId}`,
        },
        { subject: I18n.t('mapview.spaceInfo.shareSubject') },
      );
      if (result.action === Share.sharedAction) {
        // action when shared
        mp?.track('Share Bicyclehut', { spaceId: route.params?.spaceId });
        mp?.getPeople().increment('Shared bicyclehut links', 1);
      } else {
        // action when dismiss
      }
    } else {
      Clipboard.setString(`https://app.biketti.tech/c/space/${route.params.spaceId}`);
      setCopiedSnackbarVisible(true);
    }
  }, [I18n, route.params?.spaceId, mp]);

  const amenities: Amenity[] = [
    {
      icon: { name: 'bicycle', set: 'material-community' },
      title: I18n.t('mapview.spaceInfo.amenities.bicycleParking'),
      tableTitle: I18n.t('mapview.spaceInfo.amenities.maxCapacity', { count: spaceInfo?.metadata?.maxCapacity }),
      value: spaceInfo?.metadata?.maxCapacity ?? undefined,
      // bookAction: () => console.log('### book parking space'),
    },
    {
      icon: { name: 'locker-multiple', set: 'material-community' },
      title: I18n.t('mapview.spaceInfo.amenities.lockers'),
      // value: 50, // TODO: Get actual locker count
      skip: !spaceInfo?.metadata?.lockers,
      // bookAction: () => console.log('### book locker'),
    },
    {
      icon: { name: 'wrench', set: 'material-community' },
      title: I18n.t('mapview.spaceInfo.amenities.serviceStation'),
      skip: !spaceInfo?.metadata?.serviceStations,
      // bookAction: () => console.log('### book service station'),
    },
    // {
    //   icon: { name: 'water-outline', set: 'material-community' },
    //   title: I18n.t('mapview.spaceInfo.amenities.washingStation'),
    // },
  ];

  const tableAmenities: Amenity[] = [
    { icon: { name: 'clock' }, title: I18n.t('mapview.spaceInfo.openIndefinitely') },
    ...amenities,
    {
      icon: { name: 'cctv', set: 'material-community' },
      title: I18n.t('mapview.spaceInfo.amenities.videoSurveillance'),
      skip: !spaceInfo?.metadata?.cameraSurveillance,
    },
    {
      icon: { name: 'parking', set: 'material-community' },
      title: I18n.t('mapview.spaceInfo.amenities.parkingResponsibility'),
    },
  ];

  const renderDetails = () => {
    return (
      <>
        <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
          {amenities.map((item) => {
            if (item.skip) return null;
            return (
              <CardBadge
                key={item.title}
                text={`${item.title}${item.value ? ': ' + item.value : ''}`}
                textColor={colors.inverseOnSurface}
                borderColor={colors.inverseOnSurface}
                icon={{ ...item.icon, color: colors.inverseOnSurface, size: 18 }}
              />
            );
          })}
        </View>
        <Spacer type="section" />
      </>
    );
  };
  return (
    <>
      <ScrollablePage
        ignoreHeaderBottomMargin
        summary={{
          title: `${spaceInfo?.title} #${route.params?.spaceId}`,
          description: renderDetails(),
          textContainerStyle: { width: 'auto' },
          titleStyle: { width: '80%' },
          button: {
            label: I18n.t('share.share'),
            onPress: handleSharePodDetails,
          },
          // icon: (props) => {
          //   return (
          //     <Image
          //       source={
          //         Images[(spaceInfo?.metadata?.imageUrl as 'BIK500x500px' | 'CYKLON500x500px') || 'BIK500x500px'] ||
          //         Images.BIK500x500px
          //       }
          //       style={{
          //         width: props.size,
          //         height: props.size,
          //       }}
          //     />
          //   );
          // },
        }}
      >
        <View>
          {/* TODO: include address somewhere? */}
          {/* <Typography variant={'h6'}>{spaceInfo?.building?.title}</Typography> */}
          {/* <Typography variant={'body2'} style={{ marginTop: 5 }}>
            {spaceInfo?.building?.address}
          </Typography> */}
          <ImageWithFallback
            style={styles.addressImage}
            source={spaceInfo?.metadata?.imageUrl ? { uri: spaceInfo.metadata.imageUrl } : undefined}
            fallback={spaceInfo?.building?.imageUrl ? { uri: spaceInfo.building.imageUrl } : undefined}
            isSourceLoading={spaceInfoResult.isLoading}
          />
        </View>
        {spaceInfo?.metadata?.additionalInfo && (
          <View style={{ paddingHorizontal: 16, marginBottom: 20 }}>
            <TextWrapper>{spaceInfo.metadata.additionalInfo}</TextWrapper>
          </View>
        )}

        {/* TODO: Handle non 24/7 spaces */}
        {/* <SpaceSchedule spaceId={route.params.spaceId} /> */}
        <View style={{ paddingHorizontal: 16 }}>
          <Typography variant="sub1" color={colors.primary} style={{ fontWeight: '700', paddingBottom: 8 }}>
            Info
          </Typography>
          {tableAmenities.map((item, index) => {
            if (item.skip) return null;
            return (
              <View
                key={item.title}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingVertical: 6,
                  borderColor: colors.primary,
                  borderBottomWidth: index === tableAmenities.length - 1 ? 0 : 1,
                }}
              >
                <UiIcon
                  {...item.icon}
                  color={colors.primary}
                  size={24}
                  style={{
                    borderColor: colors.primary,
                    borderWidth: 1,
                    paddingVertical: 2,
                    paddingHorizontal: 4,
                    borderRadius: 4,
                  }}
                />
                <Typography variant="body2" style={{ marginLeft: 8 }}>
                  {item.tableTitle ?? item.title}
                </Typography>
                {item.bookAction && (
                  // TODO: Adjust the size of this button to not increase the height of the element in the list
                  <GradientButton
                    // disabled={disabled}
                    // loading={loading}
                    icon={'calendar'}
                    style={{ marginLeft: 'auto' }}
                    buttonStyle={{ height: 'unset' as any, paddingVertical: 6, paddingHorizontal: 0 }}
                    onPress={item.bookAction}
                  >
                    {I18n.t('mapview.podInfoCard.book')}
                  </GradientButton>
                )}
              </View>
            );
          })}
        </View>
        <Spacer />
        <View>
          {spaceInfo?.metadata?.additionalImageUrls && spaceInfo.metadata.additionalImageUrls.length > 0 && (
            <ImageCarousel
              style={styles.slideshowImage}
              sources={spaceInfo.metadata.additionalImageUrls}
              fallback={spaceInfo?.building?.imageUrl ? { uri: spaceInfo.building.imageUrl } : undefined}
              isSourceLoading={spaceInfoResult.isLoading}
            />
          )}
        </View>
        <Spacer />
        {spaceInfo?.billingType === BillingType.PAY_AS_YOU_GO &&
          spaceInfo.chargeRates &&
          spaceInfo.chargeRates.length > 0 && (
            <SpacePricing chargeRates={spaceInfo.chargeRates} currency={spaceInfo.currency || Currency.EUR} />
          )}
        <View style={styles.footerAction}>
          {spaceInfo?.building?.websiteUrl && (
            <Button
              onPress={() => {
                if (spaceInfo?.building?.websiteUrl) {
                  Linking.openURL(spaceInfo?.building?.websiteUrl);
                }
              }}
              icon={uiIconSourceGenerator({ name: 'external-link', set: 'feather' })}
            >
              {I18n.t(`bookingDetail.buildings.${spaceInfo?.buildingRef}.actions.website`, {
                defaultValue: I18n.t('bookingDetail.actions.website'),
              })}
            </Button>
          )}
          {parkingTermsUrl && (
            <Button
              onPress={() => {
                Linking.openURL(parkingTermsUrl);
              }}
              icon={uiIconSourceGenerator({ name: 'external-link', set: 'feather' })}
            >
              {I18n.t(`bookingDetail.actions.terms`)}
            </Button>
          )}
        </View>
      </ScrollablePage>
      <Snackbar visible={copiedSnackbarVisible} duration={3000} onDismiss={() => setCopiedSnackbarVisible(false)}>
        {I18n.t('share.copiedToClipboard')}
      </Snackbar>
    </>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 4,
    padding: 20,
    marginTop: 26,
  },
  footerAction: {
    flexDirection: 'row',
    // marginHorizontal: -15,
    flexWrap: 'wrap',
    paddingHorizontal: 4,
    marginBottom: 20,
  },
  addressImage: {
    marginHorizontal: -20,
    height: 250,
    backgroundColor: '#ddd',
    marginBottom: 20,
  },
  slideshowImage: {
    height: 500,
    backgroundColor: '#ddd',
  },
});
